import { motion, AnimatePresence } from "framer-motion";
import { Brain, BarChart3, Users, MapPin, Briefcase } from "lucide-react";
import { useState } from "react";

// Check your import paths align with your folder structure
import { WardDemographicsPanel } from "./WardDemographicPanel";
import { WardPsychologyLecture } from "../Messaging/Narrative";
import { Panel, ValueBadge, ArchetypeCard } from "../../UtilityComponents";
import { getPersuadabilityStyle } from "../../Constants";

// Import the static archetype biography data
import archetypeSummaryData from '../../../../data/archetype_summary.json';

export const renderElectorate = ({
    currentData, 
    wardDNA, 
    activeElectorateSubTab, 
    setActiveElectorateSubTab, 
    priorityArchetypes, 
    topIssues, 
    primaryPersuasionTargetName,
    setDrawerData 
}) => {
    
    // --- ROBUST DATA DETECTION ---
    // 1. Try to find the root object
    const demographicData = currentData?.demographic_variants || currentData;
    
    // 2. Try to find the variants array within that object
    // UPDATED: Added explicit check for 'census_summary.variants' to match your data structure
    const variantsArray = Array.isArray(demographicData) 
        ? demographicData 
        : demographicData?.variants 
        || currentData?.variants 
        || currentData?.census_summary?.variants; 

    // 3. Determine if we have enough data to render
    const hasVariants = variantsArray?.length > 0;

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            
            {/* SUB-TAB NAVIGATION */}
            <div className="flex items-center justify-start space-x-8 mb-8 border-b border-slate-700/30">
                {[
                    { id: 'archetypes', label: 'Archetypes & Psychology', icon: Brain },
                    { id: 'demographics', label: 'Demographics & Density', icon: Users }
                ].map((tab) => {
                    const isActive = activeElectorateSubTab === tab.id;
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveElectorateSubTab(tab.id)}
                            className={`
                                relative pb-3 flex items-center space-x-2 text-xs font-bold uppercase tracking-widest transition-all duration-300 group
                                ${isActive ? 'text-white' : 'text-slate-500 hover:text-slate-300'}
                            `}
                        >
                            <Icon 
                                size={14} 
                                className={`transition-all duration-300 ${isActive ? "text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" : "group-hover:text-slate-300"}`}
                            />
                            
                            <span className={isActive ? "drop-shadow-[0_0_8px_rgba(255,255,255,0.6)]" : ""}>
                                {tab.label}
                            </span>

                            {isActive && (
                                <motion.div
                                    layoutId="activeElectorateSubTab"
                                    className="absolute bottom-0 left-0 right-0 h-[1px] bg-white shadow-[0_0_10px_1px_rgba(255,255,255,0.5)]"
                                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* CONTENT AREA SWITCHER */}
            <AnimatePresence mode="wait">
                {activeElectorateSubTab === 'demographics' ? (
                    <motion.div 
                        key="demographics"
                        initial={{ opacity: 0, x: 10 }} 
                        animate={{ opacity: 1, x: 0 }} 
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                    >
                        {hasVariants ? (
                            <WardDemographicsPanel data={demographicData} />
                        ) : (
                            <div className="text-center p-12 text-slate-500 italic border border-slate-800 rounded-xl bg-slate-900/50">
                                No demographic variant data available for this ward.
                            </div>
                        )}
                    </motion.div>
                ) : (
                    <motion.div 
                        key="archetypes"
                        initial={{ opacity: 0, x: -10 }} 
                        animate={{ opacity: 1, x: 0 }} 
                        exit={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col lg:flex-row gap-6"
                    >
                        {/* LEFT COLUMN (2/3): LECTURE + DATA */}
                        <div className="lg:w-2/3 flex flex-col gap-6">
                            
                            <WardPsychologyLecture archetypes={priorityArchetypes} />

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <Panel title="Ward DNA" icon={MapPin}>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-400 flex items-center"><MapPin size={14} className="mr-2" /> Location Type</span>
                                            <span className="font-semibold text-white capitalize">{wardDNA.location}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-400 flex items-center"><Briefcase size={14} className="mr-2" /> Social Grade</span>
                                            <span className="font-semibold text-white">{wardDNA.socialGrade}</span>
                                        </div>
                                        <div className="pt-3 border-t border-slate-700/50 space-y-2">
                                            <ValueBadge value={wardDNA.housing} />
                                            <ValueBadge value={wardDNA.economy} />
                                            <ValueBadge value={wardDNA.diversity} />
                                        </div>
                                    </div>
                                </Panel>

                                <Panel title="Key Concerns & Framing" icon={Brain}>
                                    <div className="space-y-6">
                                        <div className="bg-slate-700/30 rounded-lg p-4">
                                            <div className="flex items-center space-x-2 mb-3">
                                                <BarChart3 size={18} className="text-slate-400" />
                                                <h4 className="font-semibold text-slate-200">Top Concerns</h4>
                                            </div>
                                            <div className="space-y-3">
                                                {topIssues.length > 0 ? topIssues.slice(0,3).map(([issue, score]) => (
                                                    <div key={issue} className="text-sm">
                                                        <div className="flex justify-between items-center mb-1">
                                                            <span className="capitalize text-slate-300">{issue.replace(/_/g, ' ')}</span>
                                                            <span className="text-xs text-slate-400 font-mono">{(score * 100).toFixed(0)}</span>
                                                        </div>
                                                        <div className="w-full bg-slate-700 rounded-full h-1.5">
                                                            <div className="bg-accent-500 h-1.5 rounded-full" style={{ width: `${score * 100}%` }}></div>
                                                        </div>
                                                    </div>
                                                )) : (
                                                    <p className="text-sm text-slate-400 italic">No concern data available.</p>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-accent-400 font-semibold mb-1">PRIMARY MORAL AXIS</div>
                                            <div className="text-sm font-semibold text-white mb-2">
                                                {currentData?.voter_targeting?.messaging_priorities?.deep_messaging_frame?.primary_moral_axis || 'N/A'}
                                            </div>
                                            <p className="text-xs text-slate-300 leading-relaxed bg-slate-700/30 p-2 rounded border border-slate-700/50">
                                                {currentData?.voter_targeting?.messaging_priorities?.deep_messaging_frame?.framing_guidance || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </Panel>
                            </div>
                        </div>

                        {/* RIGHT COLUMN (1/3): ARCHETYPE SIDEBAR */}
                        <div className="lg:w-1/3 flex flex-col gap-4">
                             <div className="flex items-center space-x-2 pb-2 border-b border-slate-700/50 mb-2">
                                <Users size={14} className="text-slate-400" />
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Priority Target Profile</span>
                             </div>

                             <div className="flex flex-col gap-4">
                                 {priorityArchetypes.length > 0 ? priorityArchetypes.map((archetype, index) => (
                                    <ArchetypeCard
                                        key={archetype?.archetype_id || index}
                                        archetypeData={archetype}
                                        style={getPersuadabilityStyle(archetype?.campaign_targeting?.persuadability_score)}
                                        isPrimaryTarget={archetype.name === primaryPersuasionTargetName}
                                        onClick={() => {
                                            const archetypeKey = archetype.name?.toUpperCase().replace(/[ -]/g, '_') || '';
                                            const summaryData = archetypeSummaryData[archetypeKey];
                                            
                                            if (typeof setDrawerData === 'function') {
                                                setDrawerData({ 
                                                    isOpen: true, 
                                                    type: 'wardArchetype', 
                                                    data: { wardSpecificData: archetype, biographyData: summaryData } 
                                                });
                                            } else {
                                                console.error("setDrawerData is not defined or not a function.");
                                            }
                                        }}
                                    />
                                )) : (
                                    <div className="text-center p-8 text-slate-500 bg-slate-800/30 rounded-xl border border-slate-700/50 border-dashed">
                                        No archetypes found.
                                    </div>
                                )}
                             </div>
                        </div>

                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )};